import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from 'chart.js';
import { Line } from 'react-chartjs-2';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import moment from 'moment';
import * as _ from 'lodash';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import { API } from 'api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const inr = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (value) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const CARDS = [
  { key: 'eligible_customers', label: 'Eligible Customers', format: num },
  { key: 'credits_released', label: 'Wallet Recharge (Energy Credits)', format: inr },
  { key: 'credits_utilized', label: 'Wallet Utilization', format: inr },
  { key: 'charging_revenue', label: 'Charging Revenue', format: inr },
  { key: 'sessions', label: 'Charging Sessions', format: num },
  { key: 'energy_kwh', label: 'Energy Consumed (kWh)', format: num },
];

const TRENDS = [
  { key: 'registrations', label: 'Monthly Customer Registrations' },
  { key: 'credits_released', label: 'Monthly Wallet Recharge (₹)' },
  { key: 'credits_utilized', label: 'Monthly Wallet Utilization (₹)' },
  { key: 'charging_revenue', label: 'Monthly Charging Revenue (₹)' },
];

const chartOptions = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { x: { ticks: { color: '#9ba3ab' } }, y: { beginAtZero: true, ticks: { color: '#9ba3ab' } } },
};

const PartnerDashboard = () => {
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    API.getPartnerDashboard({ month })
      .then((res) => isCurrent && setData(_.get(res, 'data')))
      .catch(() => isCurrent && setData(null))
      .finally(() => isCurrent && setIsLoading(false));
    return () => {
      isCurrent = false;
    };
  }, [month]);

  const kpis = _.get(data, 'kpis', {});
  const trends = _.takeRight(_.get(data, 'trends', []), 4);
  const labels = _.map(trends, (row) => moment(row.month, 'YYYY-MM').format('MMM YY'));

  return (
    <LayoutContainer>
      <AdminHeader title="Partner Dashboard" />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
        <div className="page-content-wrapper scrollable">
          <div className="data-report--main energy-credits__main">
            <Card>
              <div className="data-report__inner ec__actions">
                <span className="ec__hint me-auto">
                  Charging revenue = amount paid by the customer excluding energy credit, counted only for sessions where energy credit was used. E.g.
                  a ₹500 session with ₹50 energy credit used counts as ₹450. All figures are aggregated; no customer details are shown.
                </span>
                <input
                  type="month"
                  className="form-control w-auto"
                  value={month}
                  min="2026-08"
                  max={moment().format('YYYY-MM')}
                  onChange={(e) => e.target.value && setMonth(e.target.value)}
                />
              </div>
            </Card>

            <Card>
              <div className="data-report__inner">
                <div className="ec__stats">
                  {_.map(CARDS, (card) => (
                    <div className="ec__stat" key={card.key}>
                      <div className="ec__stat-label">{card.label}</div>
                      <div className="ec__stat-value">
                        {isLoading ? <Skeleton height={15} width={60} /> : card.format(_.get(kpis, [card.key, 'month']))}
                      </div>
                      <div className="ec__hint">
                        {isLoading ? <Skeleton height={10} width={80} /> : `Lifetime: ${card.format(_.get(kpis, [card.key, 'lifetime']))}`}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ec__hint mt-2">Active customers: {isLoading ? '-' : num(_.get(kpis, 'active_customers'))}</div>
              </div>
            </Card>

            <Row>
              {_.map(TRENDS, (trend) => (
                <Col xl={6} lg={12} key={trend.key}>
                  <Card>
                    <div className="data-report__inner">
                      <div className="ec__stat-label mb-2">{trend.label}</div>
                      {isLoading ? (
                        <Skeleton height={200} />
                      ) : (
                        <Line
                          options={chartOptions}
                          data={{
                            labels,
                            datasets: [{ label: trend.label, data: _.map(trends, trend.key), borderColor: '#be210b', backgroundColor: '#be210b' }],
                          }}
                        />
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </SkeletonTheme>
    </LayoutContainer>
  );
};

export default PartnerDashboard;
